import { useMemo, useState } from 'react';
import {
	Box,
	chakra,
	Combobox,
	createListCollection,
	Drawer,
	HStack,
	Icon,
	Input,
	Text,
	VStack,
} from '@chakra-ui/react';
import { LuCheck, LuChevronDown, LuPlus } from 'react-icons/lu';
import { getInputStyles } from '@/components/ui/inputStyles';
import { dropdownScrollbarCss } from '@/components/ui/scrollbar';
import DrawerCloseButton from '@/components/ui/drawer-close-button';
import { useIsMobileViewport } from '@/hooks/ui/useIsMobileViewport';
import { humaniseTrade } from '@/components/services/model/providerDirectory';

export interface ServiceOption {
	value: string;
	label: string;
}

export interface ServiceOptionGroup {
	/** Drawn above the group's options. Empty for a list that is not divided. */
	label: string;
	options: readonly ServiceOption[];
}

interface ServiceComboboxProps {
	/**
	 * The options, in the order they are drawn, under the headings they belong to.
	 *
	 * Groups are headings and nothing else: a heading is not selectable and picking is always a
	 * role. A single group with an empty label is a plain list.
	 */
	groups: readonly ServiceOptionGroup[];
	/** The chosen option's value; empty for none. */
	value: string;
	/** What the box says while nothing is chosen. */
	placeholder: string;
	onPick: (value: string) => void;
	/**
	 * Offers a row that creates what was typed, shown while the search names no option exactly.
	 * Receives the typed text, trimmed. Without it the list is only what exists.
	 */
	onCreate?: (typed: string) => void;
}

const INPUT_STYLES = getInputStyles();

/**
 * Prefixes the create row's value, which carries the typed text itself: the list closes before the
 * pick lands, and closing clears the search. No role code can take this form.
 */
const CREATE_VALUE_PREFIX = '\u0000create:';

const createRowLabel = (typed: string) => `Create ${typed} custom service`;

/** The create row's words, the typed text set heavier so the reader sees it is their own. */
const createRowText = (typed: string) => (
	<>
		Create <chakra.span fontWeight="semibold">{typed}</chakra.span> custom service
	</>
);

const createRow = (typed: string): ServiceOption => ({
	value: `${CREATE_VALUE_PREFIX}${typed}`,
	label: createRowLabel(typed),
});

/**
 * A service picked from a list long enough to want a search, where the search is the field itself.
 *
 * The current option's label sits in the box; opening clears it for a search over the whole list,
 * typing narrows, and closing without a pick puts the label back. Only what the reader types is a
 * search: the machine also writes the box, on a pick and on closing, and those writes would
 * otherwise be taken for a query of nothing. Who is in the list is the caller's business.
 *
 * On a phone the field is a button that opens a bottom sheet with the search and the list, the
 * split every other picker on a phone makes: a floating list anchored to a field is hard to hit
 * with a thumb and fights the keyboard for the screen.
 */
const ServiceCombobox = (props: ServiceComboboxProps) => {
	const { groups, value, placeholder, onPick, onCreate } = props;
	const options = useMemo(() => groups.flatMap((group) => group.options), [groups]);
	const currentLabel = value ? (options.find((option) => option.value === value)?.label ?? humaniseTrade(value)) : '';
	const isMobile = useIsMobileViewport();
	const [isSheetOpen, setIsSheetOpen] = useState(false);

	// What is typed while the list is open; null between searches, when the box shows the label.
	const [query, setQuery] = useState<string | null>(null);
	/** The groups with the search applied, and any left holding nothing dropped with their heading. */
	const matchingGroups = useMemo(() => {
		const search = query?.trim().toLocaleLowerCase();
		if (!search) return groups;
		return groups
			.map((group) => ({
				...group,
				options: group.options.filter((option) => option.label.toLocaleLowerCase().includes(search)),
			}))
			.filter((group) => group.options.length > 0);
	}, [groups, query]);
	const matches = useMemo(() => matchingGroups.flatMap((group) => group.options), [matchingGroups]);
	const typed = query?.trim() ?? '';
	// "plum" still offers the create row beside Plumber; only a label typed out in full hides it.
	const createText =
		onCreate && typed && !options.some((option) => option.label.toLocaleLowerCase() === typed.toLocaleLowerCase())
			? typed
			: null;
	const collection = useMemo(
		() =>
			createListCollection({
				items: createText ? [...matches, createRow(createText)] : [...matches],
			}),
		[matches, createText],
	);

	if (isMobile) {
		return (
			<Drawer.Root
				placement="bottom"
				open={isSheetOpen}
				onOpenChange={({ open }) => {
					setIsSheetOpen(open);
					setQuery(open ? '' : null);
				}}
			>
				<Drawer.Trigger asChild>
					<chakra.button
						type="button"
						aria-label={currentLabel ? `Service, currently ${currentLabel}` : 'Service'}
						{...INPUT_STYLES}
						fontSize="md"
						w="full"
						display="flex"
						alignItems="center"
						justifyContent="space-between"
						gap={2}
						textAlign="left"
						color={currentLabel ? undefined : 'fg.subtle'}
					>
						<Text truncate>{currentLabel || placeholder}</Text>
						<Icon as={LuChevronDown} boxSize={4} color="brand.fg" flexShrink={0} />
					</chakra.button>
				</Drawer.Trigger>
				{/* This picker also lives inside the candidate review dialog. Keep its sheet inside that
				    dialog's focus trap, as the review reason sheets do. */}
				<Drawer.Backdrop />
				<Drawer.Positioner>
					<Drawer.Content borderTopRadius="3xl" boxShadow="lg" maxH="82dvh">
						<Drawer.Header>
							<HStack w="full" justify="space-between" align="center" gap={3}>
								<Drawer.Title textStyle="body.strong" color="brand.fg">
									Service
								</Drawer.Title>
								<DrawerCloseButton flexShrink={0} />
							</HStack>
						</Drawer.Header>
						<Drawer.Body overflowY="auto" px={4} pb={5}>
							<Input
								{...INPUT_STYLES}
								fontSize="md"
								aria-label="Search services"
								placeholder="Search services"
								value={query ?? ''}
								onChange={(event) => setQuery(event.target.value)}
								mb={3}
							/>
							{/* These are ordinary buttons: radio/listbox roles would also require arrow-key navigation. */}
							<VStack gap={1} align="stretch" role="group" aria-label="Service options">
								{matches.length === 0 && !createText ? (
									<Text px={3} py={2} fontSize="sm" color="fg.muted">
										No service matches that
									</Text>
								) : null}
								{matchingGroups.map((group) => (
									<Box key={group.label || 'all'}>
										{group.label ? (
											<Text
												textStyle="label.sm"
												color="fg.muted"
												textTransform="uppercase"
												px={2}
												pt={3}
												pb={1}
											>
												{group.label}
											</Text>
										) : null}
										{group.options.map((option) => {
											const isSelected = option.value === value;
											return (
												<chakra.button
													type="button"
													key={option.value}
													aria-label={isSelected ? `${option.label}, selected` : option.label}
													onClick={() => {
														onPick(option.value);
														setIsSheetOpen(false);
														setQuery(null);
													}}
													display="flex"
													alignItems="center"
													gap={2}
													pl={2}
													pr={4}
													py={2}
													borderRadius="3xl"
													cursor="pointer"
													textAlign="left"
													_hover={{ bg: 'surface.subtle' }}
												>
													<Box w={4} color="brand.solid" flexShrink={0}>
														{isSelected && <Icon as={LuCheck} boxSize={4} />}
													</Box>
													<Text
														textStyle="body"
														color="brand.fg"
														fontWeight={isSelected ? 'semibold' : 'normal'}
													>
														{option.label}
													</Text>
												</chakra.button>
											);
										})}
									</Box>
								))}
								{createText ? (
									<chakra.button
										type="button"
										onClick={() => {
											onCreate?.(createText);
											setIsSheetOpen(false);
											setQuery(null);
										}}
										display="flex"
										alignItems="center"
										gap={2}
										pl={2}
										pr={4}
										py={2}
										mt={1}
										borderRadius="3xl"
										cursor="pointer"
										textAlign="left"
										_hover={{ bg: 'surface.subtle' }}
									>
										<Icon as={LuPlus} boxSize={4} color="brand.solid" flexShrink={0} />
										<Text textStyle="body" color="brand.fg">
											{createRowText(createText)}
										</Text>
									</chakra.button>
								) : null}
							</VStack>
						</Drawer.Body>
					</Drawer.Content>
				</Drawer.Positioner>
			</Drawer.Root>
		);
	}

	return (
		<Combobox.Root
			collection={collection}
			value={value ? [value] : []}
			inputValue={query ?? currentLabel}
			openOnClick
			onOpenChange={(details) => setQuery(details.open ? '' : null)}
			onInputValueChange={(details) => {
				if (details.reason === 'input-change') setQuery(details.inputValue);
			}}
			onValueChange={(details) => {
				const picked = details.value[0];
				if (picked?.startsWith(CREATE_VALUE_PREFIX)) onCreate?.(picked.slice(CREATE_VALUE_PREFIX.length));
				else if (picked) onPick(picked);
				setQuery(null);
			}}
			positioning={{ strategy: 'fixed', hideWhenDetached: true }}
			// The whole catalog is the list, and a page can hold several pickers at once.
			lazyMount
			unmountOnExit
		>
			<Combobox.Control>
				<Combobox.Input
					{...INPUT_STYLES}
					fontSize="md"
					aria-label="Service"
					placeholder={currentLabel || placeholder}
				/>
				{/* The group sits over the input's right end; its padding passes clicks to the input, which
				    opens the list, rather than swallowing them. Only the chevron itself is its own target. */}
				<Combobox.IndicatorGroup pe={4} pointerEvents="none">
					<Combobox.Trigger color="brand.fg" pointerEvents="auto" />
				</Combobox.IndicatorGroup>
			</Combobox.Control>
			<Combobox.Positioner>
				{/* The panel does not scroll; the box inside it does. A scroll container paints its
				    scrollbar at its own edge, full height, and no browser clips that to a border radius,
				    so on a rounded panel the thumb ran through the corner curve and read as sitting
				    outside it. The rounded, clipping parent keeps the inner scrollbar inside the curve. */}
				<Combobox.Content minW="var(--reference-width)" borderRadius="2xl" p={0} overflow="hidden">
					<Box maxH="320px" overflowY="auto" p={2} css={dropdownScrollbarCss}>
						<Combobox.Empty px={3} py={2} fontSize="sm" color="fg.muted">
							No service matches that
						</Combobox.Empty>
						{matchingGroups.map((group) => (
							<Box key={group.label || 'all'}>
								{group.label ? (
									<Text
										textStyle="label.sm"
										color="fg.muted"
										textTransform="uppercase"
										px={3}
										pt={3}
										pb={1}
									>
										{group.label}
									</Text>
								) : null}
								{group.options.map((option) => (
									<Combobox.Item item={option} key={option.value} borderRadius="xl" textStyle="body">
										{option.label}
										<Combobox.ItemIndicator />
									</Combobox.Item>
								))}
							</Box>
						))}
						{createText ? (
							<Combobox.Item
								item={createRow(createText)}
								borderRadius="xl"
								textStyle="body"
								justifyContent="flex-start"
								gap={2}
								mt={1}
							>
								<Icon as={LuPlus} boxSize={4} color="brand.solid" flexShrink={0} />
								<span>{createRowText(createText)}</span>
							</Combobox.Item>
						) : null}
					</Box>
				</Combobox.Content>
			</Combobox.Positioner>
		</Combobox.Root>
	);
};

export default ServiceCombobox;
